import React from 'react';

import styled from '@emotion/styled';

import { HOME_STORAGE_STEPS } from './data';
import { EntranceHeadline } from './entrance_headline';
import { HeadingH2, Section, SectionContainer } from './styles';
import { MEDIA, TOKENS } from './tokens';

const StepRow = styled.div<{ $bordered?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${TOKENS.space[24]};
  /* Mobile: 32px above and below the rule. */
  padding-top: ${TOKENS.space[32]};
  padding-bottom: ${TOKENS.space[32]};
  border-bottom: ${({ $bordered }) =>
    $bordered ? `1px solid ${TOKENS.color.neutral200}` : 'none'};

  ${MEDIA.lg} {
    gap: ${TOKENS.space[32]};
    min-height: 212px;
    padding-top: 0;
    padding-bottom: ${({ $bordered }) => ($bordered ? TOKENS.space[32] : '0')};
  }
`;

const StepCopy = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepTitle = styled.h3`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodyMdSize};
  line-height: ${TOKENS.type.bodyMdLineHeight};
  font-weight: ${TOKENS.type.bodyMdWeight};
  color: ${TOKENS.color.foreground};

  ${MEDIA.lg} {
    font-size: ${TOKENS.type.headingH3Size};
    line-height: ${TOKENS.type.headingH3LineHeight};
    font-weight: ${TOKENS.type.headingH3Weight};
  }
`;

const StepDescription = styled.span`
  display: block;
  margin-top: ${TOKENS.space[8]};
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: ${TOKENS.color.foregroundSecondary};

  ${MEDIA.lg} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const StepImageWrap = styled.div`
  position: relative;
  width: 120px;
  height: 80px;
  aspect-ratio: 3 / 2;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: ${TOKENS.radius.xs};

  ${MEDIA.lg} {
    width: 180px;
    height: 120px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transform: translateZ(0);
  }
`;

const MobileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: ${TOKENS.space[40]};

  & > *:first-child {
    padding-top: 0;
  }

  & > *:last-child {
    padding-bottom: 0;
  }

  ${MEDIA.lg} {
    display: none;
  }
`;

const DesktopGrid = styled.div`
  display: none;
  margin-top: ${TOKENS.space[40]};
  gap: 88px;

  ${MEDIA.lg} {
    display: flex;
  }
`;

const DesktopColumn = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
`;

const StepImage: React.FC<{
  image: string;
  alt: string;
}> = ({ image, alt }) => <img src={image} alt={alt} />;

export const HomeStorageSection: React.FC = () => (
  <Section id="storage" $variant="storage">
    <SectionContainer>
      <EntranceHeadline as={HeadingH2} style={{ maxWidth: 830 }}>
        White-glove care, handled end to end
      </EntranceHeadline>

      <MobileList>
        {HOME_STORAGE_STEPS.map((step, index) => (
          <StepRow
            key={step.title}
            $bordered={index < HOME_STORAGE_STEPS.length - 1}
          >
            <StepCopy>
              <StepTitle>{step.mobileTitle ?? step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepCopy>
            <StepImageWrap>
              <StepImage image={step.image} alt={step.imageAlt} />
            </StepImageWrap>
          </StepRow>
        ))}
      </MobileList>

      <DesktopGrid>
        <DesktopColumn>
          {HOME_STORAGE_STEPS.slice(0, 2).map((step, index) => (
            <StepRow key={step.title} $bordered={index === 0}>
              <StepCopy>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCopy>
              <StepImageWrap>
                <StepImage image={step.image} alt={step.imageAlt} />
              </StepImageWrap>
            </StepRow>
          ))}
        </DesktopColumn>
        <DesktopColumn>
          {HOME_STORAGE_STEPS.slice(2).map((step, index) => (
            <StepRow key={step.title} $bordered={index === 0}>
              <StepCopy>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCopy>
              <StepImageWrap>
                <StepImage image={step.image} alt={step.imageAlt} />
              </StepImageWrap>
            </StepRow>
          ))}
        </DesktopColumn>
      </DesktopGrid>
    </SectionContainer>
  </Section>
);
